import { TOAST_UPDATE_PERSONAL_INFO } from '@/constants/toast';
import { useConfirmUserCredentialMutation, useStartUpdateUserCredential } from '@/queries/user.queries';
import { useToast } from '@cmpsr/components';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { object, string } from 'yup';
import { OnDone } from '../../edit-email-address.controller';

export default function useModalVerifyEmailController(newEmail: string, onDone: OnDone<void>) {
  const form = useForm({
    defaultValues: { otp: '' },
    resolver: yupResolver(object().shape({ otp: string().required().min(6).max(6) })),
  });
  const confirmCredentialMutation = useConfirmUserCredentialMutation();
  const startUpdateCredentialMutation = useStartUpdateUserCredential();
  const toast = useToast();
  const [countdown, setCountdown] = useState(60);
  const [isSubmitAllowed, setIsSubmitAllowed] = useState(true);

  useEffect(() => {
    const timeout = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timeout);
  }, []);

  function handleResendCode() {
    startUpdateCredentialMutation
      .mutateAsync({ credential: newEmail })
      .then(() => {
        setCountdown(60);
        setIsSubmitAllowed(true);
        toast({
          ...TOAST_UPDATE_PERSONAL_INFO,
          description: 'A new code has been sent!',
          status: 'success',
        });
      })
      .catch((error) => {
        console.error(error);
        toast({
          ...TOAST_UPDATE_PERSONAL_INFO,
          title: `Error ${error.response?.data?.statusCode ?? ''}`,
          description: error.response?.data?.message ?? error.message,
          status: 'error',
        });
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function _handleVerificationCodeError(error: any) {
    console.error(error);
    let errorMessage = '';
    switch (error.response?.status) {
      case 400:
        errorMessage = 'Invalid verification code';
        break;
      case 410:
        errorMessage = 'This code is expired. Click on resend code to request a new one code';
        setIsSubmitAllowed(false);
        break;
      case 422:
        errorMessage = 'Too many attempts. Click on resend code to request a new one code';
        setIsSubmitAllowed(false);
        break;
      default:
        errorMessage = error.message;
        break;
    }
    form.setError('otp', { message: errorMessage });
  }

  const handleSubmit = form.handleSubmit((values) => {
    confirmCredentialMutation
      .mutateAsync({ credential: newEmail, code: values.otp })
      .then(() => {
        toast({
          ...TOAST_UPDATE_PERSONAL_INFO,
          description: 'Email address has been successfully changed',
          status: 'success',
        });
        onDone();
      })
      .catch(_handleVerificationCodeError);
  });

  return { form, handleResendCode, handleSubmit, countdown, showResend: countdown <= 0, isSubmitAllowed };
}
